import type { FocusEvent } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, RotateCw } from 'lucide-react';
import { z } from 'zod';

import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/dialog';
import { cls } from '@/utils/cls';

import type { User } from '../entities/user';
import { EditUserMutation } from '../mutations/edit-user';
import { GetRolesQuery } from '../queries/get-roles';
import { UsermanagementMetadataQuery } from '../queries/get-usermanagement-metadata';

export type EditUserDialogProps = {
  user: User | undefined;
  onClose: () => void;
};

export function EditUserDialog({ user, onClose }: EditUserDialogProps) {
  return (
    <>
      <Dialog className="mt-24" open={Boolean(user)} onClose={onClose}>
        {user ? <EditUserForm user={user} onClose={onClose} /> : null}
      </Dialog>
    </>
  );
}

type EditUserFormProps = {
  user: User;
  onClose: () => void;
};

const FormSchema = z.object({
  id: z.string(),
  active: z.boolean(),
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  email: z.string().min(1, 'Required').email('Invalid email address'),
  roleId: z.string().min(1, 'Required'),
});

type FormInputs = z.infer<typeof FormSchema>;

export function EditUserForm({ user, onClose }: EditUserFormProps) {
  const rolesQuery = GetRolesQuery.useQuery({ page: 1, pageSize: 1000 });
  const userManagementMetadata = UsermanagementMetadataQuery.useQuery();
  const editUser = EditUserMutation.useMutation();
  const form = useForm<FormInputs>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      id: user.id,
      active: user.active,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roleId: user.role.id,
    },
  });

  function handleClose() {
    onClose();
  }

  function handleEmailBlur(event: FocusEvent<HTMLInputElement>) {
    const email = event.target.value.trim();

    // Don't show confirmation if metadata isn't available
    if (!userManagementMetadata.data) {
      return;
    }

    // Don't show confirmation if email is empty or invalid
    if (!email) {
      return;
    }

    // Validate email format without throwing
    const emailValidation = z.string().email().safeParse(email);
    if (!emailValidation.success) {
      return;
    }

    // Extract domain from email
    const emailDomain = email.split('@')[1]?.toLowerCase();
    const companyDomain = userManagementMetadata.data.domain.toLowerCase();

    // Don't show confirmation if domains match
    if (emailDomain === companyDomain) {
      return;
    }

    // Show confirmation for external email
    const confirmed = window.confirm(
      `This email address (${email}) doesn't match your organization domain (@${companyDomain}). Do you still want to add this user?`,
    );

    if (!confirmed) {
      // Clear the email field if user cancels
      form.resetField('email');
    }
  }

  async function handleSubmit(data: FormInputs) {
    await toast.promise(editUser.mutateAsync(data), {
      pending: 'Editing user...',
      success: 'User updated successfully!',
      error: 'Failed to update user. Please try again.',
    });
    handleClose();
    form.reset();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogBody className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="active"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-start gap-2">
                  <FormControl>
                    <Switch
                      value={field.value}
                      onChange={field.onChange}
                      aria-label="Set user active status"
                    />
                  </FormControl>
                  <FormLabel>Active</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={
                      userManagementMetadata.data
                        ? `john.doe@${userManagementMetadata.data.domain}`
                        : 'john.doe@accure.net'
                    }
                    onBlur={handleEmailBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="roleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {rolesQuery.data?.roles.map((role) => (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                              </SelectItem>
                            )) ?? null}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => rolesQuery.refetch()}
                      disabled={rolesQuery.isFetching}
                      title="Reload roles"
                    >
                      <RotateCw
                        className={cls('size-4', {
                          'animate-spin': rolesQuery.isFetching,
                        })}
                      />
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="bg-primary-500 hover:bg-primary-400"
            type="submit"
            disabled={editUser.isLoading}
          >
            {editUser.isLoading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Editing
              </>
            ) : (
              'Edit'
            )}
          </Button>
        </DialogActions>
      </form>
    </Form>
  );
}
